import type { SWRConfiguration } from "swr";
import type { AxiosRequestConfig } from "axios";
import dappConfig from "./dapp.config";

export type Strategies =
  | "default"
  | "cached"
  | "non-refreshable"
  | "interval-refresh"
  | "long-interval-refresh"
  | "10m-interval-refresh"
  | "90s-interval-refresh"
  | "fully-cached"
  | "on-mount";

export type axiosApiTypes = "v2" | "utils" | "internal" | "internal-api";

const baseHeaders = {
  "content-type": "application/json",
  "cache-control": "public, s-maxage=1200, stale-while-revalidate=600",
  "response-content-type": "application/json",
};

export const axiosRequestConfig: Record<axiosApiTypes, AxiosRequestConfig> = {
  v2: {
    baseURL: "/api/proxy/",
    headers: {
      ...baseHeaders,
    },
    redirect: "manual",
  } as AxiosRequestConfig,
  "internal-api": {
    headers: {
      ...baseHeaders,
    },
    redirect: "manual",
  } as AxiosRequestConfig,
  internal: {
    baseURL: "/api/",
    headers: baseHeaders,
    redirect: "manual",
  } as AxiosRequestConfig,
  utils: {
    baseURL: "https://api-utils.superform.xyz",
    headers: {
      ...baseHeaders,
      accept: "application/json",
      "access-token": dappConfig.api.utilsAccessToken,
    },
    redirect: "manual",
  } as AxiosRequestConfig,
};

export const swrConfig: Record<Strategies, SWRConfiguration> = {
  default: {
    shouldRetryOnError: true, // retry on error is disabled
    loadingTimeout: 60 * 1000, // 30 seconds timeout for loading
    focusThrottleInterval: 10 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 10 seconds)
    refreshWhenHidden: true,
    refreshWhenOffline: false,
    revalidateIfStale: true,
    revalidateOnFocus: true,
    keepPreviousData: true,
    revalidateOnMount: true,
    revalidateOnReconnect: true,
  },
  "interval-refresh": {
    refreshInterval: 60 * 1000, // 30 seconds refresh interval
    shouldRetryOnError: true, // retry on error is disabled
    errorRetryCount: 2,
    loadingTimeout: 30 * 1000, // 30 seconds timeout for loading
    focusThrottleInterval: 10 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 10 seconds)
    dedupingInterval: 30 * 1000, // every next call to the endpoint will require a pause for at least 30 seconds
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    keepPreviousData: true,
    revalidateOnReconnect: true,
  },
  "long-interval-refresh": {
    refreshInterval: 180 * 1000, // 180 seconds refresh interval
    shouldRetryOnError: true, // retry on error is disabled
    errorRetryCount: 2,
    loadingTimeout: 30 * 1000, // 30 seconds timeout for loading
    focusThrottleInterval: 10 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 10 seconds)
    dedupingInterval: 30 * 1000, // every next call to the endpoint will require a pause for at least 30 seconds
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    keepPreviousData: true,
    revalidateOnReconnect: true,
  },
  "90s-interval-refresh": {
    refreshInterval: 90 * 1000, // 90 seconds refresh interval
    shouldRetryOnError: false, // retry on error is disabled
    errorRetryCount: 0,
    loadingTimeout: 60 * 1000, // 60 seconds timeout for loading
    focusThrottleInterval: 2 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 2 seconds)
    dedupingInterval: 1000, // every next call to the endpoint will require a pause for at least 1 second
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    keepPreviousData: true,
    revalidateOnMount: true,
    revalidateOnReconnect: true,
  },
  "10m-interval-refresh": {
    refreshInterval: 600 * 1000, // 10 minutes refresh interval
    shouldRetryOnError: true, // retry on error is disabled
    errorRetryCount: 2,
    loadingTimeout: 30 * 1000, // 30 seconds timeout for loading
    focusThrottleInterval: 10 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 10 seconds)
    dedupingInterval: 30 * 1000, // every next call to the endpoint will require a pause for at least 30 seconds
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    keepPreviousData: true,
    revalidateOnReconnect: true,
  },
  cached: {
    shouldRetryOnError: false, // retry on error is enabled
    errorRetryInterval: 1000,
    errorRetryCount: 3,
    loadingTimeout: 30 * 1000, // 30 seconds timeout for loading
    focusThrottleInterval: 10 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 10 seconds)
    dedupingInterval: 1000,
    refreshInterval: 60 * 60 * 1000,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    keepPreviousData: true,
    revalidateOnReconnect: false,
  },
  "fully-cached": {
    shouldRetryOnError: true, // retry on error is enabled
    errorRetryInterval: 1000,
    errorRetryCount: 3,
    loadingTimeout: 60 * 1000, // 60 seconds timeout for loading
    focusThrottleInterval: 10 * 1000, // 10 seconds throttle interval (queries would be secured from the parallel calls for 10 seconds)
    dedupingInterval: 0,
    keepPreviousData: true,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  },
  "non-refreshable": {
    shouldRetryOnError: true, // retry on error is enabled
    errorRetryInterval: 1000,
    errorRetryCount: 3,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    keepPreviousData: true,
    revalidateOnMount: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  },
  "on-mount": {
    refreshInterval: 0,
    shouldRetryOnError: false, // retry on error is disabled
    loadingTimeout: 30 * 1000, // 30 seconds timeout for loading
    dedupingInterval: 3 * 1000, // every next call to the endpoint will require a pause for at least 3 seconds
    refreshWhenHidden: false,
    keepPreviousData: true,
    refreshWhenOffline: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateOnMount: true,
  },
} as const;
